import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { medicalProfessionalsService } from "../../services/medicalProfessionalsService";
import { useAuth } from "../../contexts/AuthContext";
import authService from "../../services/authService";

const MedicalProfessionalsList = () => {
  const [loading, setLoading] = useState(true);
  const [medicalProfessionals, setMedicalProfessionals] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, mp: null });
  const [activationDialog, setActivationDialog] = useState({
    open: false,
    mp: null,
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  // FIXED: Get auth context - canManageMedicalProfessionals is a boolean, not a function
  const {
    user,
    isAdmin: contextIsAdmin,
    canManageMedicalProfessionals,
  } = useAuth();

  useEffect(() => {
    fetchMedicalProfessionals();
    fetchCurrentUser();
  }, [page, rowsPerPage, searchQuery]);

  const fetchCurrentUser = async () => {
    try {
      const user = await authService.getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  const fetchMedicalProfessionals = async () => {
    setLoading(true);
    try {
      const response =
        await medicalProfessionalsService.getMedicalProfessionals(
          page * rowsPerPage,
          rowsPerPage
        );

      if (Array.isArray(response)) {
        // Filter by search query on frontend if needed
        let filteredData = response;
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          filteredData = response.filter(
            (mp) =>
              mp.user?.full_name?.toLowerCase().includes(query) ||
              mp.mp_category?.toLowerCase().includes(query) ||
              mp.medical_license_number?.toLowerCase().includes(query) ||
              mp.npi_number?.toLowerCase().includes(query)
          );
        }

        setMedicalProfessionals(filteredData);
        setTotalCount(filteredData.length);
      } else {
        setMedicalProfessionals([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching medical professionals:", error);
      setMedicalProfessionals([]);
      setError("Failed to load medical professionals");
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Helper functions - use the boolean values from context
  const isAdmin = () => {
    return contextIsAdmin || canManageMedicalProfessionals;
  };

  const needsActivation = (mp) => {
    return mp.user && (!mp.user.is_active || mp.user.status === "pending");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0); // Reset to first page on new search
  };

  const handleDeleteClick = (mp) => {
    setDeleteDialog({ open: true, mp });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, mp: null });
  };

  const handleDeleteConfirm = async () => {
    try {
      await medicalProfessionalsService.deleteMedicalProfessional(
        deleteDialog.mp.id
      );

      // Remove the deleted item from the local state
      setMedicalProfessionals((prev) =>
        prev.filter((mp) => mp.id !== deleteDialog.mp.id)
      );
      setTotalCount((prev) => Math.max(0, prev - 1));

      setSuccess(
        `Medical professional "${deleteDialog.mp.user?.full_name}" deleted successfully`
      );
      setDeleteDialog({ open: false, mp: null });

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error deleting medical professional:", error);
      setError(error.message || "Failed to delete medical professional");
      setDeleteDialog({ open: false, mp: null });
    }
  };

  const handleActivationClick = (mp) => {
    setActivationDialog({ open: true, mp });
  };

  const handleSendActivationEmail = async () => {
    try {
      await medicalProfessionalsService.sendActivationEmail(
        activationDialog.mp.id
      );
      setSuccess(
        `Activation email sent to ${
          activationDialog.mp.user?.email || "medical professional"
        }`
      );
      setActivationDialog({ open: false, mp: null });

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error sending activation email:", error);
      setError(error.message || "Failed to send activation email");
      setActivationDialog({ open: false, mp: null });
    }
  };

  const handleActivationCancel = () => {
    setActivationDialog({ open: false, mp: null });
  };

  const getCategoryColor = (category) => {
    const colors = {
      nurse: "primary",
      doctor: "secondary",
      technician: "info",
      phlebotomist: "success",
      other: "default",
    };
    return colors[category?.toLowerCase()] || "default";
  };

  const formatCategory = (category) => {
    if (!category) return "N/A";
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          Medical Professionals
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate("/medical-professionals/create")}
          disabled={!isAdmin()}
        >
          Add Medical Professional
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search medical professionals by name, category, license number, or NPI"
          variant="outlined"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>License Number</TableCell>
                <TableCell>NPI Number</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {medicalProfessionals.length > 0 ? (
                medicalProfessionals
                  .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                  .map((mp) => (
                    <TableRow key={mp.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {mp.user?.full_name || "N/A"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {mp.user?.email || "N/A"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatCategory(mp.mp_category)}
                          color={getCategoryColor(mp.mp_category)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {mp.medical_license_number || "N/A"}
                      </TableCell>
                      <TableCell>{mp.npi_number || "N/A"}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={mp.user?.status || "Active"}
                            color={mp.user?.is_active ? "success" : "warning"}
                            size="small"
                          />
                          {needsActivation(mp) && isAdmin() && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => handleActivationClick(mp)}
                              sx={{ fontSize: "0.7rem", py: 0.5, px: 1 }}
                            >
                              Send Activation
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {mp.vehicle_number ? (
                          <Box>
                            <Typography variant="body2">
                              {mp.vehicle_number}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {mp.vehicle_make} {mp.vehicle_model}
                            </Typography>
                          </Box>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          onClick={() =>
                            navigate(`/medical-professionals/${mp.id}`)
                          }
                          title="View Details"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          color="secondary"
                          onClick={() =>
                            navigate(`/medical-professionals/${mp.id}/edit`)
                          }
                          title="Edit"
                          disabled={!isAdmin()}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(mp)}
                          title="Delete"
                          disabled={!isAdmin()}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No medical professionals found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete medical professional "
            {deleteDialog.mp?.user?.full_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activation Email Confirmation Dialog */}
      <Dialog
        open={activationDialog.open}
        onClose={handleActivationCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Activation Email</DialogTitle>
        <DialogContent>
          <Typography>
            Send activation email to "{activationDialog.mp?.user?.full_name}" at{" "}
            {activationDialog.mp?.user?.email}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The medical professional will receive an email with a link to
            activate their account and set their password.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActivationCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSendActivationEmail}
            color="primary"
            variant="contained"
          >
            Send Activation Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalProfessionalsList;
